/**
 * Повернення після входу (UX-06; links-tabs.md): посилання без входу → `/login?next=<шлях>` →
 * після входу саме цей шлях. Параметр `next` приходить з адреси, тобто від будь-кого, хто дав
 * людині посилання, тому приймаємо лише внутрішній шлях застосунку — без відкритого
 * перенаправлення на чужі сайти (SEC-156, рев'ю Security).
 *
 * Модуль без залежностей: його бере і документ входу (login.html, бюджет ≤ 100 КБ), і застосунок.
 */

export const LOGIN_PATH = '/login';

/**
 * Сторінки документа входу: повернення на них дало б коло «вхід → вхід». `/first-login/not-me` без
 * токена нічого не робить, а бюджет першого екрана без запасу, тож його тут немає.
 */
const AUTH_PAGES = /^\/(login|invitation|password-reset|email-change|first-login)$/;
/** Не екрани застосунку: API, mock-API збірки, статика. */
const NOT_SCREENS = /^\/(api|mock-api|assets)(\/|$)/;
const MAX_LENGTH = 2048;

/**
 * Безпечний шлях повернення або `null`. Приймаємо лише рядок, що починається з одного `/`,
 * без `\`, керівних символів і пробілів, який після розбору лишається на тому самому origin.
 * Повертаємо нормалізований шлях і параметри без `#…`: фрагмент у застосунку не потрібен, а
 * в посиланнях листів IAM він несе токен.
 */
export function safeNextPath(raw: unknown, origin = location.origin): string | null {
  if (typeof raw !== 'string' || raw.length === 0 || raw.length > MAX_LENGTH) return null;
  // `//host`, `/\host` і `\` браузери тлумачать як адресу іншого хоста; керівні символи й пробіли
  // (`/\t/host`) парсер URL викидає мовчки, тож перевіряємо сирий рядок, а не результат розбору.
  if (!raw.startsWith('/') || raw.startsWith('//') || /[\\\s\p{Cc}]/u.test(raw)) return null;
  let url: URL;
  try {
    url = new URL(raw, origin);
  } catch {
    return null;
  }
  if (url.origin !== origin) return null;
  const path = url.pathname;
  if (AUTH_PAGES.test(path) || NOT_SCREENS.test(path) || path.startsWith('//')) return null;
  return `${path}${url.search}`;
}

/** Адреса входу з поверненням на `next` (якщо він безпечний і не стартовий екран). */
export function loginHref(next?: unknown): string {
  const path = safeNextPath(next);
  return path && path !== '/' ? `${LOGIN_PATH}?next=${encodeURIComponent(path)}` : LOGIN_PATH;
}

/** Шлях повернення з адреси сторінки входу (`?next=`); `null` — нікуди, на стартовий екран. */
export function nextFromSearch(search = location.search): string | null {
  return safeNextPath(new URLSearchParams(search).get('next'));
}
